import { FC, useCallback, useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Market } from "@module-bindings/types";
import { assetIdKey, fixQuery, formatMarketPairLabel } from "@/lib/marketUtils";
import { TradingPaneState } from "./TradingPaneState";

const FRAC_BITS = 60n;
const DENOM = 1n << FRAC_BITS;

function priceToNumber(raw: bigint): number {
  return Number(raw >> FRAC_BITS) + Number(raw & (DENOM - 1n)) / Number(DENOM);
}

type ChartMode = "line" | "candle";
type Timeframe = "1s" | "1m" | "1h" | "1d";

interface PriceChartProps {
  compact?: boolean;
  immersiveMobile?: boolean;
  selectedMarket: Market | null;
  markets?: Market[];
  onSelectMarket?: (market: Market) => void;
}

export const PriceChart: FC<PriceChartProps> = ({ compact = false, immersiveMobile = false, selectedMarket }) => {
  const [chartMode, setChartMode] = useState<ChartMode>("line");
  const [timeframe, setTimeframe] = useState<Timeframe>("1s");

  const pairLabel = selectedMarket ? formatMarketPairLabel(selectedMarket) : "No Markets";
  const pairKey = selectedMarket
    ? `${assetIdKey(selectedMarket.tradingPair.base)}/${assetIdKey(selectedMarket.tradingPair.quote)}`
    : '';

  const query = fixQuery(tables.candlesticks.where(c => c.pairKey.eq(pairKey)), [['pairKey', 'pair_key']]);
  const [allCandles] = useTable(query);

  // Max visible candles per timeframe to keep the chart readable
  const maxCandles: Record<Timeframe, number> = { "1s": 120, "1m": 120, "1h": 168, "1d": 90 };

  // Interval in seconds per timeframe
  const tfSeconds: Record<Timeframe, number> = {
    "1s": 1, "1m": 60, "1h": 3600, "1d": 86400,
  };

  // Filter, sort, fill gaps, and trim to window
  const candles = useMemo(() => {
    if (!pairKey) return [];
    const sorted = allCandles
      .filter((c) => c.granularity === timeframe)
      .sort((a, b) => Number(a.timestamp - b.timestamp));
    if (sorted.length === 0) return [];

    // Fill gaps with flat candles (OHLC = previous close)
    const step = BigInt(tfSeconds[timeframe]);
    const filled: typeof sorted = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      const prev = filled[filled.length - 1];
      const curr = sorted[i];
      let expected = prev.timestamp + step;
      while (expected < curr.timestamp) {
        filled.push({
          ...prev,
          timestamp: expected,
          open: prev.close,
          high: prev.close,
          low: prev.close,
          close: prev.close,
          volume: 0n,
        });
        expected += step;
        // Safety: don't fill more than the max visible window
        if (filled.length > maxCandles[timeframe] * 2) break;
      }
      filled.push(curr);
    }

    const limit = maxCandles[timeframe];
    return filled.length > limit ? filled.slice(filled.length - limit) : filled;
  }, [allCandles, pairKey, timeframe]);

  // Derive display values
  const latestCandle = candles.length > 0 ? candles[candles.length - 1] : null;
  const firstCandle = candles.length > 0 ? candles[0] : null;
  const currentPrice = latestCandle ? priceToNumber(latestCandle.close) : 0;
  const openPrice = firstCandle ? priceToNumber(firstCandle.open) : currentPrice;
  const priceChange = openPrice > 0 ? ((currentPrice - openPrice) / openPrice) * 100 : 0;
  const isPositive = priceChange >= 0;
  const strokeColor = isPositive ? "var(--color-sr-lemon-muted)" : "var(--color-sr-salmon)";

  // Chart dimensions
  const svgWidth = compact ? 400 : 800;
  const svgHeight = compact ? 120 : 380;
  const pad = compact
    ? { top: 20, right: 10, bottom: 10, left: 10 }
    : { top: 20, right: 65, bottom: 5, left: 10 };
  const cw = svgWidth - pad.left - pad.right;
  const ch = svgHeight - pad.top - pad.bottom;

  // Price range across all candles
  const { minPrice, maxPrice } = useMemo(() => {
    if (candles.length === 0) return { minPrice: 0, maxPrice: 1 };
    let lo = Infinity, hi = -Infinity;
    for (const c of candles) {
      const l = priceToNumber(c.low);
      const h = priceToNumber(c.high);
      if (l < lo) lo = l;
      if (h > hi) hi = h;
    }
    if (lo === hi) { lo -= 0.001; hi += 0.001; }
    return { minPrice: lo, maxPrice: hi };
  }, [candles]);

  const range = maxPrice - minPrice;
  const yOf = useCallback(
    (price: number) => pad.top + (1 - (price - minPrice) / range) * ch,
    [pad.top, minPrice, range, ch],
  );

  // Grid lines
  const gridLines = useMemo(() => {
    const step = getGridStep(range);
    const gridMin = Math.ceil(minPrice / step) * step;
    const lines: { y: number; label: string }[] = [];
    for (let v = gridMin; v <= maxPrice; v += step) {
      lines.push({ y: yOf(v), label: v.toFixed(4) });
    }
    return lines;
  }, [minPrice, maxPrice, range, yOf]);

  // Line chart path
  const linePath = useMemo(() => {
    if (candles.length === 0) return "";
    return candles
      .map((c: any, i: number) => {
        const x = pad.left + (i / Math.max(candles.length - 1, 1)) * cw;
        const y = yOf(priceToNumber(c.close));
        return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(" ");
  }, [candles, cw, ch, pad]);

  const areaPath = useMemo(() => {
    if (!linePath || candles.length === 0) return "";
    const lastX = pad.left + ((candles.length - 1) / Math.max(candles.length - 1, 1)) * cw;
    return `${linePath} L ${lastX.toFixed(1)} ${svgHeight - pad.bottom} L ${pad.left.toFixed(1)} ${svgHeight - pad.bottom} Z`;
  }, [linePath, candles, svgHeight, pad, cw]);

  // Time labels
  const timeLabels = useMemo(() => {
    if (candles.length < 2) return [];
    const count = compact ? 4 : 8;
    const step = Math.max(1, Math.floor(candles.length / count));
    const labels: { x: number; text: string }[] = [];
    for (let i = 0; i < candles.length; i += step) {
      const x = pad.left + (i / Math.max(candles.length - 1, 1)) * cw;
      const ts = Number(candles[i].timestamp);
      const d = new Date(ts * 1000);
      const text = timeframe === "1d"
        ? `${d.getMonth() + 1}/${d.getDate()}`
        : `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
      labels.push({ x, text });
    }
    return labels;
  }, [candles, timeframe, pad, cw, compact]);

  // Candlestick bar width
  const barWidth = candles.length > 0 ? Math.max(2, (cw / candles.length) * 0.7) : 4;

  if (!selectedMarket) {
    return (
      <div className="sr-panel flex h-full min-h-[20rem] flex-col">
        <TradingPaneState
          eyebrow="Market View"
          title="Select a market"
          message="Choose a trading pair to load the chart and market context."
        />
      </div>
    );
  }

  return (
    <section
      className={`sr-panel flex h-full flex-col bg-[#23141A] ${compact ? "" : "min-h-0"}`}
      aria-label={`${pairLabel} price chart`}
    >
      {!compact && (
        <div className="flex justify-end px-5 pt-5 pb-3">
          <div className="flex items-center gap-1.5">
            {(["1s", "1m", "1h", "1d"] as const).map((tf) => (
              <button key={tf} onClick={() => setTimeframe(tf)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-colors ${
                  timeframe === tf
                    ? "bg-sr-neutral-900 text-sr-lemon-muted border-sr-border-subtle"
                    : "text-sr-neutral-500 border-transparent hover:text-sr-neutral-300"
                }`}
              >{tf.toUpperCase()}</button>
            ))}
            <div className="w-px h-5 bg-sr-border-subtle mx-1" />
            <button onClick={() => setChartMode("line")}
              className={`sr-focus-ring rounded-md p-2 transition-colors ${chartMode === "line" ? "text-sr-lemon-muted bg-sr-neutral-900" : "text-sr-neutral-500 hover:bg-sr-neutral-900 hover:text-sr-neutral-300"}`}
              aria-label="Line chart view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M4 13L7.5 6L10 10L14 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button onClick={() => setChartMode("candle")}
              className={`sr-focus-ring rounded-md p-2 transition-colors ${chartMode === "candle" ? "text-sr-lemon-muted bg-sr-neutral-900" : "text-sr-neutral-500 hover:bg-sr-neutral-900 hover:text-sr-neutral-300"}`}
              aria-label="Candlestick view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <rect x="3" y="5" width="3" height="8" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="7.5" y="3" width="3" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
                <rect x="12" y="7" width="3" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
              </svg>
            </button>
            <button className="sr-focus-ring rounded-md p-2 text-sr-neutral-500 transition-colors hover:bg-sr-neutral-900 hover:text-sr-neutral-300" aria-label="Analytics view">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M3 3V15H15" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                <path d="M6 12L9 8L12 10L15 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Chart */}
      <div
        className={
          compact
            ? "px-2 pb-2"
            : immersiveMobile
              ? "relative flex-1 px-1 pt-3 pb-2"
              : "relative min-h-0 flex-1 px-1 pb-1"
        }
      >
        {candles.length === 0 ? (
          <div className={`flex items-center justify-center ${compact ? "h-[120px]" : "h-full"}`}>
            <span className="text-sm text-sr-neutral-500">No trade data yet</span>
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            className={
              compact
                ? "w-full"
                : immersiveMobile
                  ? "h-[46vh] min-h-[320px] w-full"
                  : "h-full w-full"
            }
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="chartAreaGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={strokeColor} stopOpacity="0.08" />
                <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* Grid */}
            {gridLines.map((gl, i) => (
              <line key={i} x1={pad.left} y1={gl.y} x2={svgWidth - pad.right} y2={gl.y}
                stroke="var(--color-sr-border-subtle)" strokeWidth="0.5" strokeDasharray="4 4" opacity="0.3" />
            ))}

            {chartMode === "line" ? (
              <>
                <path d={areaPath} fill="url(#chartAreaGrad)" />
                <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </>
            ) : (
              /* Candlestick bars */
              candles.map((c: any, i: number) => {
                const x = pad.left + (i / Math.max(candles.length - 1, 1)) * cw;
                const o = priceToNumber(c.open);
                const cl = priceToNumber(c.close);
                const h = priceToNumber(c.high);
                const l = priceToNumber(c.low);
                const bullish = cl >= o;
                const color = bullish ? "var(--color-sr-mint)" : "var(--color-sr-salmon)";
                const bodyTop = yOf(Math.max(o, cl));
                const bodyBot = yOf(Math.min(o, cl));
                const bodyH = Math.max(1, bodyBot - bodyTop);
                return (
                  <g key={i}>
                    {/* Wick — upper and lower segments only, so they don't show through the body */}
                    <line x1={x} y1={yOf(h)} x2={x} y2={bodyTop} stroke={color} strokeWidth="1" />
                    <line x1={x} y1={bodyBot} x2={x} y2={yOf(l)} stroke={color} strokeWidth="1" />
                    {/* Body — always filled */}
                    <rect x={x - barWidth / 2} y={bodyTop} width={barWidth} height={bodyH}
                      fill={color} stroke={color} strokeWidth="0.5" />
                  </g>
                );
              })
            )}
          </svg>
        )}

        {/* Y-axis labels */}
        {!compact && candles.length > 0 && (
          <div className="pointer-events-none absolute inset-y-0 right-3">
            {gridLines.map((gl, i) => (
              <span key={i} className="absolute right-0 -translate-y-1/2 text-[11px] text-sr-neutral-500"
                style={{ top: `${(gl.y / svgHeight) * 100}%`, fontFamily: "var(--font-display)" }}>
                {gl.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* X-axis time labels */}
      {!compact && timeLabels.length > 0 && (
        <div className="flex justify-between px-4 pb-3 text-[10px] text-sr-neutral-500 tabular-nums">
          {timeLabels.map((t, i) => (
            <span key={i}>{t.text}</span>
          ))}
        </div>
      )}
    </section>
  );
};

function getGridStep(range: number): number {
  if (range > 3) return 1;
  if (range > 1) return 0.5;
  if (range > 0.1) return 0.05;
  if (range > 0.01) return 0.005;
  return 0.001;
}
